class TabsCustom extends HTMLElement {
  constructor() {
    super();
    this.tabList = this.querySelector('[role="tablist"]');
    this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
    this.isVerticalTablist = this.tabList.getAttribute('aria-orientation') === 'vertical';
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');

    // If no tab is active by default, activate the first tab.
    if (!this.activeTab) {
      this.activeTab = this.tabs[0];
      this.activateTab(this.activeTab);
    }

    this.addListeners();
  }

  addListeners() {
    this.tabList.addEventListener('click', this.handleClick.bind(this));
    this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handles 'click' events on the tablist.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (!evt.target.matches('[role="tab"]') || evt.target === this.activeTab) return;
    this.activateTab(evt.target);
  }

  /**
   * Handles 'keydown' events on the tablist.
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    switch (evt.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        evt.preventDefault();
        if (!this.isVerticalTablist) {
          this.switchTabOnKeyPress(evt.key);
        }
        break;

      case 'ArrowUp':
      case 'ArrowDown':
        evt.preventDefault();
        if (this.isVerticalTablist) {
          this.switchTabOnKeyPress(evt.key);
        }
        break;

      // Activate the first tab.
      case 'Home':
        evt.preventDefault();
        this.activateTab(this.tabs[0]);
        break;

      // Activate the last tab.
      case 'End':
        evt.preventDefault();
        this.activateTab(this.tabs[this.tabs.length - 1]);
        break;
    }
  }

  /**
   * Activates the next or previous tab according to the key pressed.
   * @param {string} key - Key pressed.
   */
  switchTabOnKeyPress(key) {
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      // If active tab is the last tab.
      if (this.activeTab === this.tabs[this.tabs.length - 1]) {
        // Activate first tab.
        this.activateTab(this.tabs[0]);
      } else {
        // Activate next tab.
        this.activateTab(this.activeTab.nextElementSibling);
      }
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      // If active tab is the first tab.
      if (this.activeTab === this.tabs[0]) {
        this.activateTab(this.tabs[this.tabs.length - 1]);
      } else {
        // Activate the previous tab.
        this.activateTab(this.activeTab.previousElementSibling);
      }
    }
  }

  /**
   * Activates a tab.
   * @param {Element} tab - Tab element (button).
   */
  activateTab(tab) {
    this.deactivateActiveTab();

    TabsCustom.setTabState(tab, true);
    tab.removeAttribute('tabindex');
    this.activeTab = tab;

    if (document.activeElement.matches('.tablist__tab')) {
      tab.focus();
    }
  }

  /**
   * Deactivates the currently active tab.
   */
  deactivateActiveTab() {
    TabsCustom.setTabState(this.activeTab, false);
    this.activeTab.setAttribute('tabindex', '-1');
    this.activeTab = null;
  }

  /**
   * Sets the active state of a tab and associated content panel.
   * @param {Element} tab - Tab element (button).
   * @param {boolean} active - Set the tab as active.
   */
  static setTabState(tab, active) {
    tab.setAttribute('aria-selected', active);

    const panelId = tab.getAttribute('aria-controls');
    document.getElementById(panelId).hidden = !active;
    if (active) {
      document.getElementById(panelId).dispatchEvent(new Event('tab-activated'));
    }
  }
}

customElements.define('tabbed-content-custom', TabsCustom);

class DetailsDisclosureCustom extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.panel = this.toggle.nextElementSibling;
    this.init();
  }

  init() {
    // Check if the content element has a CSS transition.
    if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
      this.toggle.addEventListener('click', this.handleToggle.bind(this));
      this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
    }
  }

  /**
   * Handles 'click' events on the summary element.
   * @param {object} evt - Event object.
   */
  handleToggle(evt) {
    evt.preventDefault();

    if (!this.disclosure.open) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Handles 'transitionend' events on the details element.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;

    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }

    this.panel.removeAttribute('style');
  }

  /**
   * Adds inline 'height' style to the content element, to trigger open transition.
   */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
    this.panel.dispatchEvent(new Event('panel-activated'));
  }

  /**
   * Opens the details element.
   */
  open() {
    // Set content 'height' to zero before opening the details element.
    this.panel.style.height = '0';

    // Open the details element
    this.disclosure.open = true;

    // Set content 'height' to its scroll height, to enable CSS transition.
    this.addContentHeight();
  }

  /**
   * Closes the details element.
   */
  close() {
    // Set content height to its scroll height, to enable transition to zero.
    this.addContentHeight();

    // Add class to enable styling of content or toggle icon before or during close transition.
    this.disclosure.classList.add('is-closing');

    // Set content height to zero to trigger the transition.
    // Slight delay required to allow scroll height to be applied before changing to '0'.
    setTimeout(() => {
      this.panel.style.height = '0';
    });
  }
}

customElements.define('details-disclosure-custom', DetailsDisclosureCustom);

// Initialize truncate buttons
document.querySelectorAll('.js-truncateBtn').forEach(($button) => {
  $button.addEventListener('click', handleTruncateClick);
});

// Initialize tab content
function initTabContent() {
  const jsTruncate = this.querySelector('.js-truncate');
  if (!jsTruncate) return;

  const jsTruncateBtn = this.querySelector('.js-truncateBtn');
  
  // Get real height by temporarily making content visible if needed
  const originalDisplay = jsTruncate.style.display;
  const originalHeight = jsTruncate.style.height;
  
  jsTruncate.style.display = 'block';
  jsTruncate.style.height = 'auto';
  
  const contentHeight = jsTruncate.offsetHeight;
  const shouldTruncate = jsTruncate.dataset.truncate < contentHeight - 10;
  
  // Restore original styles
  jsTruncate.style.display = originalDisplay;
  jsTruncate.style.height = originalHeight;

  if (shouldTruncate) {
    jsTruncate.classList.add('tablist__trancated');
    jsTruncate.style.height = `${jsTruncate.dataset.truncate}px`;
    jsTruncateBtn.classList.remove('truncate-read-more-btn--hidden');
  } else {
    jsTruncateBtn.classList.add('truncate-read-more-btn--hidden');
  }
}

// Handle truncate button click
function handleTruncateClick() {
  const container = this.parentElement;
  const elements = {
    dots: container.querySelector(".js-dots"),
    moreText: container.querySelector(".js-more"),
    truncate: container.querySelector(".js-truncate")
  };

  const isExpanded = this.classList.contains('active');
  
  // Toggle elements visibility
  elements.dots.style.display = isExpanded ? 'inline' : 'none';
  elements.moreText.style.display = isExpanded ? 'none' : 'block';
  elements.truncate.style.display = isExpanded ? 'block' : 'none';
  
  // Update button
  this.innerHTML = isExpanded ? 'Read more' : 'Read less';
  this.classList.toggle('active');
}

// Initialize panels
let boundTab = initTabContent.bind(document.querySelector('.tablist__panel'));
boundTab();

let boundPanel = initTabContent.bind(document.querySelector('.product-tabs .disclosure__panel'));
boundPanel();

// Add event listeners for panel activation
document.querySelectorAll('.product-tabs .tablist__panel').forEach(($tab) => {
  $tab.addEventListener('tab-activated', initTabContent.bind($tab));
});

document.querySelectorAll('.product-tabs .disclosure__panel').forEach(($tab) => {
  $tab.addEventListener('panel-activated', initTabContent.bind($tab));
});

// Show more/less functionality for specifications table
document.querySelectorAll('.js-show-more').forEach(button => {
  button.addEventListener('click', function() {
    const hiddenRows = this.closest('.specifications-table').querySelectorAll('.js-hidden-row');
    const isExpanded = this.getAttribute('aria-expanded') === 'true';
    
    hiddenRows.forEach(row => {
      row.classList.toggle('hidden');
    });
    
    this.setAttribute('aria-expanded', !isExpanded);
    this.textContent = !isExpanded ? this.dataset.textLess : this.dataset.textMore;
  });
});

